import { useEffect, useState } from 'react';
import { Lock, LogOut, Check, X, Trash2, RefreshCw, Calendar, MessageSquare, Mail, Star, FileText, Phone, Car, MapPin } from 'lucide-react';
import SectionLabel from '../components/SectionLabel';
import {
  verifyAdmin, fetchStats,
  fetchAdminBookings, updateBookingStatus,
  fetchAdminReviews, moderateReview, deleteReview,
  fetchAdminContacts, fetchAdminNewsletter,
  fetchAdminQuoteRequests, updateQuoteStatus, deleteQuoteRequest,
} from '../lib/api';

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: RefreshCw },
  { id: 'quotes', label: 'Offertes', icon: FileText },
  { id: 'bookings', label: 'Afspraken', icon: Calendar },
  { id: 'reviews', label: 'Reviews', icon: Star },
  { id: 'contact', label: 'Contact', icon: MessageSquare },
  { id: 'newsletter', label: 'Nieuwsbrief', icon: Mail },
];

const STATUS_OPTIONS = ['pending', 'confirmed', 'completed', 'cancelled'];
const QUOTE_STATUSES = [
  { id: 'new', label: 'Nieuw', color: 'bg-[#2f6bff]/15 text-[#7aa3ff] border-[#2f6bff]/30' },
  { id: 'contacted', label: 'Contact', color: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' },
  { id: 'closed', label: 'Gesloten', color: 'bg-green-500/15 text-green-400 border-green-500/30' },
];

const Admin = () => {
  const [authed, setAuthed] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('admin_token') || '');
  const [error, setError] = useState('');
  const [tab, setTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [quoteFilter, setQuoteFilter] = useState('all');

  useEffect(() => {
    if (token) tryLogin(token);
    // eslint-disable-next-line
  }, []);

  const tryLogin = async (t) => {
    localStorage.setItem('admin_token', t);
    try {
      await verifyAdmin();
      setAuthed(true);
      setError('');
      loadAll();
    } catch {
      setAuthed(false);
      setError('Ongeldig token');
    }
  };

  const loadAll = async () => {
    try {
      const [s, b, r, c, n, q] = await Promise.all([
        fetchStats(), fetchAdminBookings(), fetchAdminReviews(), fetchAdminContacts(), fetchAdminNewsletter(), fetchAdminQuoteRequests()
      ]);
      setStats(s); setBookings(b); setReviews(r); setContacts(c); setSubs(n); setQuotes(q);
    } catch (e) { console.error(e); }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    setToken('');
    setAuthed(false);
  };

  if (!authed) {
    return (
      <div className="bg-[#0a0a0b] text-white pt-32 pb-24 min-h-screen">
        <div className="max-w-md mx-auto px-6 lg:px-12">
          <SectionLabel>Admin</SectionLabel>
          <h1 className="mt-5 text-4xl font-medium tracking-tight">Beheerderslogin</h1>
          <form onSubmit={(e) => { e.preventDefault(); tryLogin(token); }} className="mt-8 space-y-4">
            <div>
              <label className="block text-white/60 text-xs tracking-wider uppercase mb-2">Admin Token</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input type="password" value={token} onChange={e => setToken(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-md pl-10 pr-4 py-3 text-white text-sm focus:outline-none focus:border-[#2f6bff]" placeholder="Voer uw admin token in" />
              </div>
            </div>
            {error && <div className="text-red-400 text-sm">{error}</div>}
            <button type="submit" className="w-full px-6 py-3 rounded-md bg-[#2f6bff] hover:bg-[#1f56e8] text-white text-sm font-medium">Inloggen</button>
          </form>
        </div>
      </div>
    );
  }

  const renderTab = () => {
    if (tab === 'dashboard' && stats) {
      const items = [
        { label: 'Offerte-aanvragen', value: stats.quote_requests, icon: FileText },
        { label: 'Afspraken', value: stats.bookings, icon: Calendar },
        { label: 'Reviews (totaal)', value: stats.reviews_total, icon: Star },
        { label: 'Reviews (te modereren)', value: stats.reviews_pending, icon: Star },
        { label: 'Contact berichten', value: stats.contacts, icon: MessageSquare },
        { label: 'Nieuwsbrief subs', value: stats.newsletter, icon: Mail },
      ];
      return (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(it => (
            <div key={it.label} className="p-6 rounded-xl border border-white/10 bg-white/[0.02] flex items-center justify-between">
              <div>
                <div className="text-white/50 text-xs uppercase tracking-wider">{it.label}</div>
                <div className="mt-2 text-3xl font-medium">{it.value}</div>
              </div>
              <it.icon className="w-8 h-8 text-[#2f6bff]" />
            </div>
          ))}
        </div>
      );
    }

    if (tab === 'quotes') {
      const setStatus = async (id, status) => {
        try {
          await updateQuoteStatus(id, status);
          loadAll();
        } catch (e) { console.error(e); }
      };
      const remove = async (id) => {
        if (window.confirm('Deze offerte-aanvraag definitief verwijderen?')) {
          try {
            await deleteQuoteRequest(id);
            loadAll();
          } catch (e) { console.error(e); }
        }
      };
      const filtered = quoteFilter === 'all' ? quotes : quotes.filter(q => q.status === quoteFilter);
      const counts = {
        all: quotes.length,
        new: quotes.filter(q => q.status === 'new').length,
        contacted: quotes.filter(q => q.status === 'contacted').length,
        closed: quotes.filter(q => q.status === 'closed').length,
      };
      return (
        <div className="space-y-4" data-testid="admin-quotes-tab">
          <div className="flex flex-wrap gap-2">
            {[
              { id: 'all', label: 'Alle' },
              { id: 'new', label: 'Nieuw' },
              { id: 'contacted', label: 'Contact' },
              { id: 'closed', label: 'Gesloten' },
            ].map(f => {
              const active = quoteFilter === f.id;
              return (
                <button
                  key={f.id}
                  onClick={() => setQuoteFilter(f.id)}
                  data-testid={`quote-filter-${f.id}`}
                  className={`px-3.5 py-2 rounded-md text-xs font-medium border transition-colors ${
                    active
                      ? 'bg-[#2f6bff] border-[#2f6bff] text-white'
                      : 'bg-white/[0.02] border-white/10 text-white/70 hover:border-white/25'
                  }`}
                >
                  {f.label} <span className="opacity-60 ml-1">{counts[f.id]}</span>
                </button>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="text-white/50 py-10 text-center border border-dashed border-white/10 rounded-xl">
              Geen offerte-aanvragen in deze filter.
            </div>
          )}

          {filtered.map(q => {
            const badge = QUOTE_STATUSES.find(s => s.id === q.status) || QUOTE_STATUSES[0];
            return (
              <div key={q.id} className="p-5 rounded-xl border border-white/10 bg-white/[0.02]" data-testid={`quote-row-${q.id}`}>
                <div className="flex flex-wrap justify-between gap-4">
                  <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center flex-wrap gap-2">
                      <span className="text-white font-medium text-base">{q.name}</span>
                      <span className={`text-[10px] tracking-wider uppercase px-2 py-0.5 rounded border ${badge.color}`}>
                        {badge.label}
                      </span>
                      {!q.email_sent && (
                        <span className="text-[10px] tracking-wider uppercase px-2 py-0.5 rounded border bg-red-500/10 text-red-400 border-red-500/25">
                          E-mail niet verzonden
                        </span>
                      )}
                    </div>
                    <div className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      <a href={`mailto:${q.email}`} className="text-white/70 hover:text-white inline-flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5 text-white/40" /> {q.email}
                      </a>
                      <a href={`tel:${q.phone}`} className="text-white/70 hover:text-white inline-flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5 text-white/40" /> {q.phone}
                      </a>
                      <div className="text-white/70 inline-flex items-center gap-1.5">
                        <Car className="w-3.5 h-3.5 text-white/40" /> {q.vehicle}
                        {q.license_plate && <span className="text-white/40 text-xs">({q.license_plate})</span>}
                      </div>
                      <div className="text-white/70 inline-flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-white/40" /> {q.location}
                      </div>
                    </div>
                    <div className="mt-3">
                      <div className="text-[10px] tracking-wider uppercase text-white/40">Behandeling</div>
                      <div className="text-white text-sm mt-0.5">{q.treatment}</div>
                    </div>
                    {q.notes && (
                      <div className="mt-3">
                        <div className="text-[10px] tracking-wider uppercase text-white/40">Opmerking</div>
                        <p className="text-white/70 text-sm mt-0.5 whitespace-pre-line">{q.notes}</p>
                      </div>
                    )}
                    <div className="text-white/30 text-[11px] mt-3">
                      {new Date(q.created_at).toLocaleString('nl-NL', { dateStyle: 'medium', timeStyle: 'short' })}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 min-w-[180px]">
                    <label className="text-[10px] tracking-wider uppercase text-white/40">Status wijzigen</label>
                    <div className="grid grid-cols-3 gap-1.5">
                      {QUOTE_STATUSES.map(s => {
                        const active = q.status === s.id;
                        return (
                          <button
                            key={s.id}
                            onClick={() => setStatus(q.id, s.id)}
                            data-testid={`quote-status-${q.id}-${s.id}`}
                            className={`px-2 py-2 rounded-md text-[11px] font-medium border transition-colors ${
                              active
                                ? s.color + ' border-current'
                                : 'bg-white/[0.02] border-white/10 text-white/60 hover:border-white/25'
                            }`}
                          >
                            {s.label}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      onClick={() => remove(q.id)}
                      data-testid={`quote-delete-${q.id}`}
                      className="mt-2 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Verwijderen
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    if (tab === 'bookings') {
      const update = async (id, status) => { await updateBookingStatus(id, status); loadAll(); };
      return (
        <div className="space-y-3">
          {bookings.length === 0 && <div className="text-white/50">Geen afspraken.</div>}
          {bookings.map(b => (
            <div key={b.id} className="p-5 rounded-xl border border-white/10 bg-white/[0.02]">
              <div className="flex flex-wrap justify-between gap-4">
                <div>
                  <div className="text-white font-medium">{b.name} <span className="text-white/40 text-sm">— {b.email}</span></div>
                  <div className="text-white/60 text-sm mt-1">{b.service} ({b.category})</div>
                  <div className="text-white/40 text-xs mt-1">{b.date} {b.time} · Tel {b.phone} · {b.car}</div>
                  {b.notes && <div className="text-white/50 text-sm mt-2 italic">&ldquo;{b.notes}&rdquo;</div>}
                </div>
                <div>
                  <select value={b.status} onChange={e => update(b.id, e.target.value)} className="bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white">
                    {STATUS_OPTIONS.map(s => <option key={s} value={s} className="bg-[#111114]">{s}</option>)}
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (tab === 'reviews') {
      const approve = async (id, val) => { await moderateReview(id, val); loadAll(); };
      const remove = async (id) => { if (confirm('Verwijderen?')) { await deleteReview(id); loadAll(); } };
      return (
        <div className="space-y-3">
          {reviews.length === 0 && <div className="text-white/50">Nog geen reviews.</div>}
          {reviews.map(r => (
            <div key={r.id} className="p-5 rounded-xl border border-white/10 bg-white/[0.02]">
              <div className="flex flex-wrap justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="text-white font-medium">{r.name}</span>
                    <span className="text-[#2f6bff] text-sm">{'★'.repeat(r.rating)}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${r.approved ? 'bg-green-500/15 text-green-400' : 'bg-yellow-500/15 text-yellow-400'}`}>
                      {r.approved ? 'Goedgekeurd' : 'In afwachting'}
                    </span>
                  </div>
                  <div className="text-white/40 text-xs mt-1">{r.email} · {r.service}</div>
                  <p className="text-white/70 text-sm mt-2">&ldquo;{r.text}&rdquo;</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => approve(r.id, !r.approved)} className="px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 text-white text-sm flex items-center gap-1">
                    {r.approved ? <><X className="w-4 h-4" /> Verberg</> : <><Check className="w-4 h-4" /> Keur goed</>}
                  </button>
                  <button onClick={() => remove(r.id)} className="px-3 py-2 rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm flex items-center gap-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (tab === 'contact') {
      return (
        <div className="space-y-3">
          {contacts.length === 0 && <div className="text-white/50">Geen berichten.</div>}
          {contacts.map(c => (
            <div key={c.id} className="p-5 rounded-xl border border-white/10 bg-white/[0.02]">
              <div className="text-white font-medium">{c.name} <span className="text-white/40 text-sm">— {c.email}</span></div>
              {c.phone && <div className="text-white/50 text-xs mt-1">Tel: {c.phone}</div>}
              <div className="flex flex-wrap gap-2 mt-2">
                {c.vehicle_type && <span className="px-2 py-0.5 rounded bg-[#2f6bff]/15 text-[#7aa3ff] text-xs">{c.vehicle_type}</span>}
                {c.interest && <span className="px-2 py-0.5 rounded bg-white/10 text-white/70 text-xs">{c.interest}</span>}
              </div>
              <p className="text-white/70 text-sm mt-3 whitespace-pre-line">{c.message}</p>
            </div>
          ))}
        </div>
      );
    }

    if (tab === 'newsletter') {
      return (
        <div className="space-y-2">
          {subs.length === 0 && <div className="text-white/50">Geen abonnees.</div>}
          {subs.map(s => (
            <div key={s.id} className="p-4 rounded-md border border-white/10 bg-white/[0.02] flex justify-between text-sm">
              <span className="text-white">{s.email}</span>
              <span className="text-white/40 text-xs">{new Date(s.created_at).toLocaleDateString('nl-NL')}</span>
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="bg-[#0a0a0b] text-white pt-32 pb-24 min-h-screen">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="flex flex-wrap justify-between items-center gap-4 mb-10">
          <div>
            <SectionLabel>Admin</SectionLabel>
            <h1 className="mt-3 text-4xl md:text-5xl font-medium tracking-tight">Dashboard</h1>
          </div>
          <div className="flex gap-2">
            <button onClick={loadAll} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-white/10 hover:bg-white/5 text-sm"><RefreshCw className="w-4 h-4" /> Vernieuwen</button>
            <button onClick={logout} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-white/10 hover:bg-white/5 text-sm"><LogOut className="w-4 h-4" /> Uitloggen</button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 border-b border-white/10 mb-8">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`px-5 py-3 text-sm flex items-center gap-2 border-b-2 -mb-px transition-colors ${tab === t.id ? 'text-white border-[#2f6bff]' : 'text-white/50 border-transparent hover:text-white'}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {renderTab()}
      </div>
    </div>
  );
};

export default Admin;
